import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateParts,
	unLinkTemplateParts,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import type { TemplatePart } from "../../../../models/TemplatePart";
import { PartsTable } from "./parts-tables";

interface Props {
	searchParts: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	isLoading: boolean;
	setShowLinkResourcesToAssetDialog: Function;
}

export const PartPicker: React.FC<Props> = ({
	searchParts,
	show,
	onHide,
	TemplateId,
	isLoading,
	setShowLinkResourcesToAssetDialog,
}) => {
	const {
		state: { listAllParts, templatePartsList },
		actions: { setTemplatePartsToAdd },
	} = useTemplatesContext();

	const [selectedParts, setSelectedParts] = useState<TemplatePart[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	useEffect(() => {
		if (show) {
			searchParts();
		}
	}, [show]);

	useEffect(() => {
		if (show) {
			setSelectedParts(templatePartsList);
		}
	}, [show, templatePartsList]);

	const handleSelectionChanged = (newSelectedParts: TemplatePart[]) => {
		setSelectedParts(newSelectedParts);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const partsToAdd = selectedParts.filter(
			(part) =>
				!templatePartsList.some((woPart) => woPart.PartName === part.PartName),
		);

		const partsToRemove = templatePartsList.filter(
			(part) =>
				!selectedParts.some(
					(selectedPart) => selectedPart.PartName === part.PartName,
				),
		);

		if (partsToAdd.length > 0) {
			const newParts = partsToAdd.map((part) => ({
				PartName: part.PartName ?? "",
				IsAssetResource: false,
			}));
			setTemplatePartsToAdd(partsToAdd);
			try {
				// setShowLinkResourcesToAssetDialog(true);
				const res = await linkTemplateParts(TemplateId as number, newParts);
				if (res.ok) {
					setSuccessMessage("Parts added successfully");
				} else setError(res.message);
				setShowLinkResourcesToAssetDialog(true);
			} catch (err) {
				setError("Failed to add parts");
			}
		}

		if (partsToRemove.length > 0) {
			const partsToRemoveFormatted = partsToRemove.map((part) => ({
				PartName: part.PartName ?? "",
				IsAssetResource: part.IsAssetResource,
			}));
			try {
				await unLinkTemplateParts(TemplateId as number, partsToRemoveFormatted);
				setSuccessMessage("Parts removed successfully");
			} catch (err) {
				setError("Failed to remove parts");
			}
		}

		setLoadingSave(false);
		if (partsToAdd.length > 0 || partsToRemove.length > 0) {
			onHide(true);
		} else {
			onHide(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						PARTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<PartsTable
						allParts={listAllParts}
						templateParts={templatePartsList}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button
								startIcon={loadingSave && <CircularProgress size="1rem" />}
								disabled={loadingSave}
								variant="contained"
								color="primary"
								onClick={handleSave}
							>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
